import { computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { TaskActivitiesDataService } from '@app/shared/services/task-activities-data.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { UrlSegment } from '@app/shared/types/enums/url-segment.enum';
import { TaskActivity } from '@app/shared/types/models/task-activities/task-activity';
import { Task } from '@app/shared/types/models/task/task.model';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

/**
 * ActiveTaskService - Manages the currently active task state.
 *
 * This service handles task selection, URL synchronization, and provides access to the active task's related data.
 *
 * Scope: Route-level (`providers` on the project tasks parent route in `project-tasks.routing.ts`).
 *
 * Usage: Used by task-related components to get/set the currently active task and navigate between tasks and subtasks.
 *
 * Architecture:
 * - {@link ActiveTaskService}: Active task state management, URL synchronization, task navigation
 * - {@link ActiveProjectService}: Project id for subtask queries
 * - {@link TasksActionsService}: User actions, mutations, side effects (toasts, modals)
 * - {@link TasksDataService}: Data access, queries, getters, store management
 */
@Injectable()
export class ActiveTaskService {
  private readonly taskActivitiesDataService: TaskActivitiesDataService = inject(TaskActivitiesDataService);
  private readonly tasksDataService: TasksDataService = inject(TasksDataService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly _activeTaskId: WritableSignal<string | undefined> = signal(undefined);
  private readonly _descriptionHasUnsavedChanges: WritableSignal<boolean> = signal(false);

  public readonly activeTask: Signal<Task> = computed(() =>
    this._activeTaskId() ? this.tasksDataService.getById(this._activeTaskId())() : undefined
  );
  public readonly activeTaskId: Signal<string> = this._activeTaskId.asReadonly();
  public readonly activeTaskActivities: Signal<TaskActivity[]> = computed(() =>
    this._activeTaskId() ? this.taskActivitiesDataService.getTaskActivities(this._activeTaskId())() : []
  );
  public readonly activeTaskSubtasks: Signal<Task[]> = computed(() =>
    this._activeTaskId()
      ? this.tasksDataService.getTasksByParent(this.activeProjectService.activeProjectId(), this._activeTaskId())()
      : []
  );

  public constructor() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const taskId = this.getTaskIdFromRoute(this.activatedRoute);
      if (taskId && this._activeTaskId() !== taskId) {
        this._activeTaskId.set(taskId);
      } else if (!taskId && this._activeTaskId()) {
        this._activeTaskId.set(undefined);
      }
    });
    const taskId = this.getTaskIdFromRoute(this.activatedRoute);
    if (taskId) {
      this._activeTaskId.set(taskId);
    }
  }

  private getTaskIdFromRoute(route: ActivatedRoute): string | null {
    for (let currentRoute = route; currentRoute; currentRoute = currentRoute.firstChild) {
      const taskId = currentRoute.snapshot.paramMap.get('task-id');
      if (taskId) {
        return taskId;
      }
    }
    return null;
  }

  /** Syncs description dirty state from the details panel for close / switch guards. */
  public setDescriptionHasUnsavedChanges(hasUnsavedChanges: boolean): void {
    this._descriptionHasUnsavedChanges.set(hasUnsavedChanges);
  }

  public async setActiveTask(task: Task): Promise<boolean> {
    if (!task) {
      return Promise.reject(new Error('Task is required'));
    }
    if (this._activeTaskId() && this._activeTaskId() !== task.id) {
      const canProceed = await this.confirmDiscardUnsavedChangesIfNeeded();
      if (!canProceed) {
        return false;
      }
    }
    this._activeTaskId.set(task?.id);
    this._descriptionHasUnsavedChanges.set(false);
    return this.navigateToActiveTask(task);
  }

  /**
   * Clears the active task selection and navigates to the project tasks list.
   * Always fully closes details — including when the active item is a subtask.
   */
  public async clearActiveTask(): Promise<boolean> {
    const canProceed = await this.confirmDiscardUnsavedChangesIfNeeded();
    if (!canProceed) {
      return false;
    }
    this._activeTaskId.set(undefined);
    this._descriptionHasUnsavedChanges.set(false);
    return this.navigateToTasksList();
  }

  private async confirmDiscardUnsavedChangesIfNeeded(): Promise<boolean> {
    if (!this._descriptionHasUnsavedChanges()) {
      return true;
    }

    const confirmed = await firstValueFrom(
      this.dialogService
        .openConfirmationDialog({
          title: this.translateService.instant('unsaved-changes-modal-title'),
          desc: this.translateService.instant('unsaved-changes')
        })
        .afterClosed()
    );

    return confirmed === true;
  }

  /**
   * Unsets the active task: for a subtask navigates to the parent; for a root task clears selection.
   * Prefer {@link clearActiveTask} when closing the details panel.
   */
  public unsetActiveTask(): Promise<boolean> {
    const currentTask = this.activeTask();
    const parent: Task = currentTask?.parentTask();
    if (parent) {
      return this.setActiveTask(parent);
    }
    return this.clearActiveTask();
  }

  private navigateToActiveTask(task: Task): Promise<boolean> {
    const navigationBase = this.getTasksNavigationBase(task.project().id);
    if (!navigationBase) {
      return Promise.reject(new Error('Cannot navigate to active task'));
    }
    return this.router.navigate([...navigationBase.pathSegments, task.id], {
      queryParamsHandling: navigationBase.queryParamsHandling
    });
  }

  private navigateToTasksList(): Promise<boolean> {
    const projectId = this.activeProjectService.activeProjectId();
    if (!projectId) {
      return Promise.reject(new Error('Cannot navigate to tasks list without an active project'));
    }
    const navigationBase = this.getTasksNavigationBase(projectId);
    if (!navigationBase) {
      return Promise.reject(new Error('Cannot navigate to tasks list'));
    }
    return this.router.navigate(navigationBase.pathSegments, {
      queryParamsHandling: navigationBase.queryParamsHandling
    });
  }

  private getTasksNavigationBase(
    projectId: string
  ): { pathSegments: string[]; queryParamsHandling: 'merge' | 'replace' } | null {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const segments = urlTree.root.children['primary']?.segments || [];
    const projectIndex = segments.findIndex(segment => segment.path === UrlSegment.PROJECTS);

    if (projectIndex === -1) {
      return null;
    }

    const basePath = segments
      .slice(0, projectIndex + 1)
      .map(segment => segment.path)
      .join('/');
    const queryParamsHandling = projectId === this.activeProjectService.activeProjectId() ? 'merge' : 'replace';

    return {
      pathSegments: [basePath, projectId, UrlSegment.TASKS],
      queryParamsHandling
    };
  }
}
